import { useTranslation } from "react-i18next";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { SwitchInput } from "./ui/switchInput";
import { cn } from "../lib/utils";
import { ChevronDown, ChevronUp, Trash2, RotateCcw, Image, X, Paperclip, Mic, Square, Play, Pause, FileText } from "lucide-react";
import { useSession } from "../context/SessionContext";
import { Plus } from "lucide-react";
import QuickMessageDialog from "../components/QuickMessageDialog";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from "./ui/tooltip";
import toast from "react-hot-toast";

interface QuickMessage {
  id: number;
  message_es: string;
  message_en: string;
  title_es: string;
  title_en: string;
}

interface MessageInputProps {
  onSendMessage: (message: string, imageFile?: File, audioFile?: File, documentFile?: File) => void;
  disabled?: boolean;
  aiBlock: boolean;
  toggleAiBlock: () => void;
  isBusy?: boolean;
  selectedUserData?: any;
}

export const shouldRenderMessage = (msg: any): boolean => {
  if (!msg) return false;
  
  if (msg.messageType === "image" && msg.media_url) {
    return true;
  }
  
  if (msg.messageType === "audio" && msg.media_url) {
    return true;
  }
  
  if (msg.messageType === "document" && msg.media_url) {
    return true;
  }
  
  if (msg.messageType === "text" && msg.body) {
    return true;
  }
  
  if (!msg.messageType && msg.body) {
    return true;
  }
  
  return false;
};

export default function MessageInput({
  onSendMessage,
  disabled,
  aiBlock,
  toggleAiBlock,
  isBusy = false,
  selectedUserData,
}: MessageInputProps) {
  const { t } = useTranslation();
  const { session, language } = useSession();
  const [message, setMessage] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [quickMessages, setQuickMessages] = useState<QuickMessage[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [isResettingThread, setIsResettingThread] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);

  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);

  const [isRecording, setIsRecording] = useState(false);
  const [selectedAudio, setSelectedAudio] = useState<File | null>(null);
  const [audioPreview, setAudioPreview] = useState<string | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const audioPreviewRef = useRef<HTMLAudioElement | null>(null);

  const [selectedDocument, setSelectedDocument] = useState<File | null>(null);
  const [documentPreview, setDocumentPreview] = useState<string | null>(null);

  const [addQuickOpen, setAddQuickOpen] = useState(false);
  const [newQuickMessage, setNewQuickMessage] = useState("");
  const [newQuickTitle, setNewQuickTitle] = useState("");
  const [savingQuick, setSavingQuick] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toDelete, setToDelete] = useState<{ id: number; text: string } | null>(
    null
  );

  const allowedMimeTypes = [
    'image/jpeg',
    'image/jpg', 
    'image/png'
  ];

  const allowedAudioMimeTypes = [
    'audio/mpeg',
    'audio/mp3',
    'audio/ogg',
    'audio/webm',
    'audio/aac',
    'audio/amr',
    'audio/3gpp',
    'audio/mp4',
    'audio/wav'
  ];

  const allowedDocumentMimeTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'application/zip',
    'application/x-zip-compressed'
  ];

  const handleImageSelect = useCallback((file: File) => {
    if (!allowedMimeTypes.includes(file.type)) {
      alert(t("invalid_file_type") || "Solo se permiten archivos de imagen (JPG, JPEG, PNG)");
      return;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert(t("file_too_large") || "El archivo es demasiado grande (máximo 10MB)");
      return;
    }

    setSelectedImage(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  }, [t, allowedMimeTypes]);

  const handleImageRemove = useCallback(() => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageSelect(file);
    }
  }, [handleImageSelect]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      let mimeType = 'audio/webm';
      
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
        mimeType = 'audio/ogg;codecs=opus';
      } else {
        mimeType = 'audio/mp4';
      }
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        
        let extension = 'webm';
        if (mimeType.includes('ogg')) extension = 'ogg';
        else if (mimeType.includes('mp4')) extension = 'mp4';
        else if (mimeType.includes('wav')) extension = 'wav';
        
        const audioFile = new File([audioBlob], `audio_${Date.now()}.${extension}`, { type: mimeType });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        setSelectedAudio(audioFile);
        setAudioPreview(audioUrl);
        
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error(t("microphone_access_error") || "No se pudo acceder al micrófono");
    }
  }, [t]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  }, [isRecording]);

  const handleAudioFileSelect = useCallback((file: File) => {
    if (!allowedAudioMimeTypes.some(type => file.type.includes(type))) {
      alert(t("invalid_audio_type") || "Solo se permiten archivos de audio");
      return;
    }

    const maxSize = 16 * 1024 * 1024; // 16MB
    if (file.size > maxSize) {
      alert(t("audio_too_large") || "El archivo de audio es demasiado grande (máximo 16MB)");
      return;
    }

    setSelectedAudio(file);
    const audioUrl = URL.createObjectURL(file);
    setAudioPreview(audioUrl);
  }, [t, allowedAudioMimeTypes]);

  const handleAudioInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleAudioFileSelect(file);
    }
  }, [handleAudioFileSelect]);

  const handleAudioRemove = useCallback(() => {
    setSelectedAudio(null);
    setAudioPreview(null);
    setIsPlayingPreview(false);
    if (audioInputRef.current) {
      audioInputRef.current.value = '';
    }
    if (audioPreviewRef.current) {
      audioPreviewRef.current.pause();
      audioPreviewRef.current.currentTime = 0;
    }
  }, []);

  const toggleAudioPreview = useCallback(() => {
    if (!audioPreviewRef.current) return;
    
    if (isPlayingPreview) {
      audioPreviewRef.current.pause();
      setIsPlayingPreview(false);
    } else {
      audioPreviewRef.current.play();
      setIsPlayingPreview(true);
    }
  }, [isPlayingPreview]);

  useEffect(() => {
    if (audioPreview && audioPreviewRef.current) {
      audioPreviewRef.current.onended = () => {
        setIsPlayingPreview(false);
      };
    }
  }, [audioPreview]);

  const formatRecordingTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev + 1);
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => {
      const newCount = prev - 1;
      if (newCount <= 0) {
        setIsDragging(false);
      }
      return newCount;
    });
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragCounter(0);

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    
    if (imageFile) {
      handleImageSelect(imageFile);
    }
  }, [handleImageSelect]);

  // Paste handler
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const imageItem = items.find(item => allowedMimeTypes.includes(item.type));
    
    if (imageItem) {
      e.preventDefault();
      const file = imageItem.getAsFile();
      if (file) {
        handleImageSelect(file);
      }
    }
  }, [handleImageSelect, allowedMimeTypes]);

  const handleDocumentSelect = useCallback((file: File) => {
    if (!allowedDocumentMimeTypes.includes(file.type)) {
      alert(t("invalid_document_type") || "Solo se permiten documentos (PDF, Word, Excel, PowerPoint, TXT, ZIP)");
      return;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert(t("file_too_large") || "El archivo es demasiado grande (máximo 10MB)");
      return;
    }

    setSelectedDocument(file);
    setDocumentPreview(file.name);
  }, [t, allowedDocumentMimeTypes]);

  const handleDocumentRemove = useCallback(() => {
    setSelectedDocument(null);
    setDocumentPreview(null);
    if (documentInputRef.current) {
      documentInputRef.current.value = '';
    }
  }, []);

  const handleDocumentInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleDocumentSelect(file);
    }
  }, [handleDocumentSelect]);

  const fetchQuickMessages = useCallback(async () => {
    if (!session) return;
    try {
      setLoadError(false);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/callcenter/chat/fetchQuickMessages`,
        {
          method: "GET",
          headers: {
            Authorization: `${session.token_type} ${session.access_token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!res.ok) throw new Error(res.statusText);
      const data: QuickMessage[] = await res.json();
      setQuickMessages(data);
    } catch (err) {
      console.error("Error fetching quick messages:", err);
      setLoadError(true);
    }
  }, [session]);

  useEffect(() => {
    fetchQuickMessages();
  }, [fetchQuickMessages]);

  const addQuickMessage = useCallback(async () => {
    if (!session || !newQuickMessage.trim() || !newQuickTitle.trim()) return;
    try {
      setSavingQuick(true);

      const lang = (language || "es").toLowerCase();
      const isEnglish = lang.startsWith("en");

      const payload = isEnglish
        ? { 
            title_en: newQuickTitle,
            title_es: "",
            message_en: newQuickMessage, 
            message_es: ""
          }
        : { 
            title_es: newQuickTitle,
            title_en: "",
            message_es: newQuickMessage, 
            message_en: ""
          };

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/callcenter/chat/addQuickMessages`,
        {
          method: "PATCH",
          headers: {
            Authorization: `${session.token_type} ${session.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok)
        throw new Error(await res.text().catch(() => res.statusText));

      setAddQuickOpen(false);
      setNewQuickMessage("");
      setNewQuickTitle("");

      await fetchQuickMessages();
      setMenuOpen(true);
    } catch (err) {
      console.error("Error adding quick message:", err);
    } finally {
      setSavingQuick(false);
    }
  }, [session, newQuickMessage, newQuickTitle, language, fetchQuickMessages]);

  const deleteQuickMessage = useCallback(async () => {
    if (!session || !toDelete) return;
    try {
      setDeleting(true);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/callcenter/chat/deleteQuickMessage`,
        {
          method: "DELETE", 
          headers: {
            Authorization: `${session.token_type} ${session.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ id: toDelete.id }),
        }
      );
      if (!res.ok)
        throw new Error(await res.text().catch(() => res.statusText));

      setDeleteOpen(false);
      setToDelete(null);
      await fetchQuickMessages();
      setMenuOpen(true);
    } catch (err) {
      console.error("Error deleting quick message:", err);
    } finally {
      setDeleting(false);
    }
  }, [session, toDelete, fetchQuickMessages]);

  const resetUserThread = useCallback(async () => {
    if (!selectedUserData?.id || !session) return;
    
    try {
      setIsResettingThread(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/clear-thread/${selectedUserData.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${session.token_type} ${session.access_token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(await response.text().catch(() => response.statusText));
      }

      const result = await response.json();
      toast.success(t("thread_reset_success") || "Thread ID reseteado correctamente");

      onSendMessage(t("thread_reset_message") || "El thread ha sido reseteado.");

      return result;
    } catch (err: any) {
      console.error("Error resetting thread:", err);
      toast.error(t("thread_reset_error") || "Error al resetear el thread ID");
    } finally {
      setIsResettingThread(false);
    }
  }, [selectedUserData?.id, session, t, onSendMessage]);

  const handleSend = () => {
    if (isBusy) return;
    if (message.trim() || selectedImage || selectedAudio || selectedDocument) {
      onSendMessage(
        message, 
        selectedImage || undefined, 
        selectedAudio || undefined, 
        selectedDocument || undefined
      );
      setMessage("");
      handleImageRemove();
      handleAudioRemove();
      handleDocumentRemove();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const isEnglish = useMemo(
    () => (language || "es").toLowerCase().startsWith("en"),
    [language]
  );

  const filteredQuickMessages = useMemo(
    () =>
      quickMessages.filter((qm) => {
        const title = isEnglish ? qm.title_en : qm.title_es;
        return !!title && title.trim().length > 0;
      }),
    [quickMessages, isEnglish]
  );

  const triggerDisabled =
    isBusy || !aiBlock || loadError || quickMessages.length === 0;

  const canSend = (message.trim() || selectedImage || selectedAudio || selectedDocument) && aiBlock && !isBusy;

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    
    switch (ext) {
      case 'pdf':
        return <FileText className="w-5 h-5 text-red-500" />;
      case 'doc':
      case 'docx':
        return <FileText className="w-5 h-5 text-blue-500" />;
      case 'xls':
      case 'xlsx':
        return <FileText className="w-5 h-5 text-green-500" />;
      case 'ppt':
      case 'pptx':
        return <FileText className="w-5 h-5 text-orange-500" />;
      case 'txt':
        return <FileText className="w-5 h-5 text-gray-500" />;
      case 'zip':
        return <FileText className="w-5 h-5 text-purple-500" />;
      default:
        return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  return (
    <div className="relative w-full">
      {(audioPreview || imagePreview || documentPreview || isRecording) && (
        <div className="absolute bottom-full left-0 right-0 w-full bg-[#f6f7f9] py-3 rounded-t-xl">
          {audioPreview && (
            <div className="relative w-full px-4">
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-3 border">
                <button
                  onClick={toggleAudioPreview}
                  className="p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600"
                >
                  {isPlayingPreview ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </button>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-700">
                    {selectedAudio?.name || "Grabación de audio"}
                  </div>
                  {selectedAudio && (
                    <div className="text-xs text-gray-500">
                      {Math.round(selectedAudio.size / 1024)}KB
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={handleAudioRemove}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
              >
                <X className="w-3 h-3" />
              </button>
              <audio ref={audioPreviewRef} src={audioPreview} className="hidden" />
            </div>
          )}

          {imagePreview && (
            <div className="relative w-full flex justify-between items-start px-4">
              <img
                src={imagePreview}
                alt="Preview"
                className="max-w-[500px] max-h-[350px] rounded-lg border"
              />
              <button
                onClick={handleImageRemove}
                className="bg-red-500 text-white rounded-full p-1 hover:bg-red-600 flex-shrink-0"
              >
                <X className="w-3 h-3" />
              </button>
              {selectedImage && (
                <div className="absolute bottom-0 left-4 text-xs text-gray-500 mt-1">
                  {selectedImage.name} ({Math.round(selectedImage.size / 1024)}KB)
                </div>
              )}
            </div>
          )}

          {documentPreview && selectedDocument && (
            <div className="relative w-full px-4">
              <div className="flex items-center gap-3 bg-gray-100 rounded-lg p-3 border">
                <div className="flex-shrink-0">
                  {getFileIcon(selectedDocument.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-700 truncate">
                    {selectedDocument.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {Math.round(selectedDocument.size / 1024)}KB
                  </div>
                </div>
              </div>
              <button
                onClick={handleDocumentRemove}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}

          {isRecording && (
            <div className="px-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-sm font-medium text-red-700">
                    {t("recording")} {formatRecordingTime(recordingTime)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div 
        className={cn(
          "relative w-full",
          isDragging && "bg-blue-50 border-2 border-dashed border-blue-300 rounded-lg"
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {isDragging && (
          <div className="absolute inset-0 flex items-center justify-center bg-blue-50 bg-opacity-90 z-10 rounded-lg">
            <div className="text-center">
              <Image className="w-12 h-12 mx-auto text-blue-500 mb-2" />
              <p className="text-blue-700 font-medium">{t("drop_image_here") || "Suelta la imagen aquí"}</p>
            </div>
          </div>
        )}

        <div className="flex items-center bg-white rounded-xl border border-[#E5E7EC] focus-within:border-[#105BDB] pr-64 pl-2 py-2 transition-colors duration-200">
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              className={cn(
                "p-1 rounded mt-1 transition-opacity",
                triggerDisabled
                  ? "opacity-40 cursor-not-allowed"
                  : "hover:bg-gray-100 cursor-pointer"
              )}
              onClick={() => {
                if (!triggerDisabled) setMenuOpen((o) => !o);
              }}
              disabled={triggerDisabled}
            >
              {menuOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>

            {menuOpen && !triggerDisabled && (
              <div
                className="
                  absolute -left-2 bottom-12 mb-2 w-max max-w-[40rem]
                  bg-white border border-gray-200 rounded-lg shadow-lg z-10
                  overflow-hidden text-gray-700 max-h-[20rem] overflow-y-auto custom-scrollbar
                "
              >
                <div className="px-2 py-2 bg-gray-50 text-sm font-medium border-b border-gray-200 flex items-center justify-between gap-2">
                  <span className="px-2">{t("quick_messages")}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-[#105BDB] hover:bg-blue-50"
                    onClick={() => setAddQuickOpen(true)}
                    disabled={!aiBlock || isBusy}
                    aria-label={t("add_quick_message")}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                <ul className="divide-y divide-gray-100">
                  {filteredQuickMessages.length > 0 ? (
                    filteredQuickMessages.map((qm) => {
                      const title = isEnglish ? qm.title_en : qm.title_es;
                      const message = isEnglish ? qm.message_en : qm.message_es;
                      return (
                        <li
                          key={qm.id}
                          className="px-4 py-2 hover:bg-blue-50 transition-colors cursor-pointer whitespace-pre-wrap break-words text-sm"
                          onClick={() => {
                            setMessage(message);
                            setMenuOpen(false);
                          }}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="text-sm whitespace-pre-wrap break-words">
                              {title}
                            </div>
                            <button
                              type="button"
                              className={cn(
                                "p-1 rounded transition-colors",
                                "text-red-300 hover:text-red-600 active:text-red-700"
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                setToDelete({ id: qm.id, text: title });
                                setDeleteOpen(true);
                              }}
                              aria-label={
                                t("delete_quick_message") ||
                                "Eliminar frase rápida"
                              }
                              title={
                                t("delete_quick_message") ||
                                "Eliminar frase rápida"
                              }
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </li>
                      );
                    })
                  ) : (
                    <li className="px-4 py-3 text-sm text-[#6F7D95]">
                      {t("no_quick_messages") || "No hay frases rápidas aún."}
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>

          <Input
            ref={inputRef}
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={t("message_input_placeholder")}
            disabled={!aiBlock || isBusy || isRecording}
            className="flex-1 border-none bg-transparent focus:ring-0 focus:ring-[#105BDB] focus:outline-none focus:border-none disabled:opacity-50 text-[#252A32]"
          />
        </div>

        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center space-x-2 mr-1">
          <input
            ref={audioInputRef}
            type="file"
            accept={allowedAudioMimeTypes.join(',')}
            onChange={handleAudioInputChange}
            className="hidden"
          />
          
          {/* ESTO ES PARA ADJUNTAR AUDIO, LO DEJO COMENTADO PORQUE NO LO VAMOS A USAR DE MOMENTO  */}
          
          {/* {!isRecording ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => audioInputRef.current?.click()}
                    disabled={isBusy || !aiBlock || selectedAudio !== null || selectedImage !== null}
                    className="p-2 hover:bg-gray-100 rounded-md"
                  >
                    <Paperclip className="w-4 h-4 text-gray-600" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t("attach_audio") || "Adjuntar audio"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null} */}

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isBusy || !aiBlock || selectedAudio !== null || selectedImage !== null || selectedDocument !== null}
                  className={cn(
                    "p-2 rounded-md",
                    isRecording 
                      ? "bg-red-100 hover:bg-red-200 text-red-600" 
                      : "hover:bg-gray-100"
                  )}
                >
                  {isRecording ? (
                    <Square className="w-4 h-4" />
                  ) : (
                    <Mic className="w-4 h-4 text-gray-600" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {isRecording 
                  ? (t("stop_recording") || "Detener grabación")
                  : (t("record_audio") || "Grabar audio")
                }
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <input
            ref={documentInputRef}
            type="file"
            accept={allowedDocumentMimeTypes.join(',')}
            onChange={handleDocumentInputChange}
            className="hidden"
          />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => documentInputRef.current?.click()}
                  disabled={isBusy || !aiBlock || selectedAudio !== null || selectedImage !== null || selectedDocument !== null}
                  className="p-2 hover:bg-gray-100 rounded-md"
                >
                  <FileText className="w-4 h-4 text-gray-600" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t("attach_document") || "Adjuntar documento"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <input
            ref={fileInputRef}
            type="file"
            accept={allowedMimeTypes.join(',')}
            onChange={handleFileInputChange}
            className="hidden"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isBusy || !aiBlock || selectedAudio !== null || selectedImage !== null || selectedDocument !== null}
            className="p-2 hover:bg-gray-100 rounded-md"
            title={t("attach_image") || "Adjuntar imagen"}
          >
            <Image className="w-4 h-4 text-gray-600" />
          </Button>

          <SwitchInput
            checked={!aiBlock}
            onCheckedChange={() => {
              if (!isBusy) toggleAiBlock();
            }}
            disabled={isBusy}
          />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={resetUserThread}
                  disabled={isBusy || !selectedUserData?.id || isResettingThread}
                  variant="outline"
                  size="sm"
                  className={cn(
                    "rounded-md px-3",
                    (!selectedUserData?.id || isResettingThread)
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                      : "bg-white text-gray-600 hover:bg-gray-50 border-gray-300"
                  )}
                >
                  <RotateCcw className={cn("w-4 h-4", isResettingThread && "animate-spin")} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t("reset_thread_tooltip") || "Resetear el thread y enviar un mensaje al usuario"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button
            onClick={handleSend}
            disabled={!canSend}
            className={cn(
              "rounded-md",
              !canSend
                ? "bg-[#EDEFF2] text-[#D5D9E0] cursor-not-allowed"
                : "bg-[#0D2B74] text-white hover:bg-[#0B2568]"
            )}
          >
            {t("send")}
          </Button>
        </div>
      </div>

      <QuickMessageDialog
        open={addQuickOpen}
        setOpen={setAddQuickOpen}
        value={newQuickMessage}
        setValue={setNewQuickMessage}
        title={newQuickTitle}
        setTitle={setNewQuickTitle}
        t={t}
        onConfirm={addQuickMessage}
        disabled={isBusy || !aiBlock || savingQuick}
      />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="bg-white max-w-md w-full rounded-lg shadow-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-red-600" />
              <span className="text-lg text-[#13161A] font-normal">
                {t("delete_quick_message") || "Eliminar frase rápida"}
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="text-sm text-[#13161A]">
            {t("confirm_delete_quick_message") ||
              "¿Seguro que quieres eliminar esta frase?"}
            {toDelete?.text && (
              <div className="mt-2 p-2 bg-[#F7F7F9] rounded whitespace-pre-wrap break-words text-[#6F7D95]">
                {toDelete.text}
              </div>
            )}
          </div>

          <DialogFooter className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button
                variant="secondary"
                className="bg-white border rounded-lg px-4 py-2"
              >
                {t("cancel") || "Cancelar"}
              </Button>
            </DialogClose>
            <Button
              onClick={deleteQuickMessage}
              disabled={deleting}
              className={cn(
                "px-4 py-2 text-base font-normal rounded-lg",
                "bg-red-500 text-white hover:bg-red-600 active:bg-red-700"
              )}
            >
              {t("delete") || "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}